import { useState, useEffect } from 'react';
import { Buffer } from 'buffer';
import { createClient, gtv, IClient } from 'postchain-client';
import {
  createKeyStoreInteractor,
  createWeb3ProviderEvmKeyStore,
  Account,
  Session,
  ftEventEmitter,
  pendingTransferStrategies,
  EvmKeyStore,
  createInMemoryLoginKeyStore,
  KeyStoreInteractor,
  hours,
  ttlLoginRule,
} from '@chromia/ft4';

export const ethereumToChromiaAccountId = (ethAddress: string): Buffer => {
  const addressWithoutPrefix = ethAddress.substring(2);
  const addressBuffer = Buffer.from(addressWithoutPrefix, 'hex');
  const paddedBuffer = padTo20Bytes(addressBuffer);
  return gtv.gtvHash(paddedBuffer);
};

const padTo20Bytes = (buffer: Buffer): Buffer => {
  if (buffer.length >= 20) return buffer;
  const paddedBuffer = Buffer.alloc(20);
  buffer.copy(paddedBuffer, 20 - buffer.length);
  return paddedBuffer;
};

export function createEClient() {
  return createClient({
    directoryNodeUrlPool: ["https://system.chromaway.com:7740"],
    blockchainRid: "15C0CA99BEE60A3B23829968771C50E491BD00D2E3AE448580CD48A8D71E7BBA",
  });
}

export const useChromiaSession = (storageType: string) => {
  const [session, setSession] = useState<Session | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [ethAddress, setEthAddress] = useState<string>('');
  const [keyStoreInteractor, setKeyStoreInteractor] = useState<KeyStoreInteractor | null>(null);
  const [provisionalAccountId, setProvisionalAccountId] = useState<string>('');
  const [strategyType, setStrategyType] = useState<string | null>(null);

  useEffect(() => {
    const initializeSession = async () => {
      const client = await createEClient();
      const store = await createWeb3ProviderEvmKeyStore(window.ethereum);
      const keyStoreInteractor_ = createKeyStoreInteractor(client, store);
      setKeyStoreInteractor(keyStoreInteractor_);
      const { getAccounts, getSession } = keyStoreInteractor_;
      
      const ethAddr = '0x' + store.address.toString('hex');
      setEthAddress(ethAddr);

      const accountsData = await getAccounts();
      setAccounts(accountsData);

      if (!accountsData.length) {
        const provisionalId = ethereumToChromiaAccountId(ethAddr).toString('hex');
        setSession(null);
        setProvisionalAccountId(provisionalId);
        
        try {
          const strategies = await client.query(
            pendingTransferStrategies(Buffer.from(provisionalId, 'hex'))
          );
          setStrategyType(
            strategies.includes("open") ? "open" : 
            strategies.includes("fee") ? "fee" : 
            null
          );
        } catch (error) {
          console.error("Error checking pending strategies:", error);
          setStrategyType(null);
        }
        return;
      }

      const newSession = await getSession(accountsData[0].id);
      setSession(newSession);
    };

    initializeSession();

    ftEventEmitter.on('KeyStoreChange', () => {
      initializeSession();
    });
  }, [storageType]);

  return { session, accounts, ethAddress, provisionalAccountId, strategyType, keyStoreInteractor };
}; 

export async function loginUsingKeyStoreInteractor(keyStoreInteractor: KeyStoreInteractor, accountId: Buffer) {
   const keyStore = createInMemoryLoginKeyStore();

   const { session } = await keyStoreInteractor.login({
        accountId,
        loginKeyStore: keyStore,
        config: {
          flags: ["T"],
          rules: ttlLoginRule(hours(1)),
        }
      });

      return session;
}